import React, { useRef, useState } from "react";
import { Button, Space, Upload, message } from "antd";
import { CameraOutlined, InboxOutlined, UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../../commons/GeneralErrorAlert";
import { uploadBelege } from "./api";
import shrinkImage from "./shrinkImage";
import { ACCEPT, MAX_FILES } from "./constants";

/**
 * Drag-and-drop area, file button and a camera button for phones. Files are sent as soon as
 * they are chosen; `onUploaded(results)` gets the server's per-file answer.
 */
const BelegUpload = ({ onUploaded, compact = false, disabled = false }) => {
  const { t, i18n } = useTranslation("accounting");
  const [busy, setBusy] = useState(false);
  const cameraInput = useRef(null);
  const language = (i18n.language || "de").slice(0, 2);

  const send = async (rawFiles, source) => {
    const list = Array.from(rawFiles || []).slice(0, MAX_FILES);
    if (!list.length) return;
    if (rawFiles.length > MAX_FILES)
      message.warning(t("At most {{n}} files per upload", { n: MAX_FILES }));
    setBusy(true);
    try {
      const files = await Promise.all(list.map(shrinkImage));
      const res = await uploadBelege(files, { source, language });
      const results = res.data.results || [];
      results.forEach(r => {
        if (!r.ok) message.warning(`${r.name}: ${r.error}`);
      });
      const created = results.filter(r => r.ok).length;
      if (created) message.success(t("{{n}} file(s) uploaded, reading…", { n: created }));
      onUploaded?.(results);
    } catch (err) {
      // A 409 (all duplicates) still carries per-file results worth showing.
      const results = err?.response?.data?.results;
      if (results) {
        results.forEach(r => message.warning(`${r.name}: ${r.error}`));
        onUploaded?.(results);
      } else generalErrorAlert(err);
    } finally {
      setBusy(false);
    }
  };

  const camera = (
    <>
      <Button
        icon={<CameraOutlined />}
        loading={busy}
        disabled={disabled}
        onClick={() => cameraInput.current?.click()}
      >
        {t("Photo")}
      </Button>
      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        style={{ display: "none" }}
        onChange={e => {
          send(e.target.files, "mobile");
          e.target.value = "";
        }}
      />
    </>
  );

  if (compact) {
    return (
      <Space wrap>
        <Upload
          multiple
          accept={ACCEPT}
          showUploadList={false}
          disabled={disabled || busy}
          beforeUpload={(file, fileList) => {
            // One request for the whole selection, not one per file.
            if (file === fileList[0]) send(fileList, "upload");
            return false;
          }}
        >
          <Button type="primary" icon={<UploadOutlined />} loading={busy} disabled={disabled}>
            {t("Upload receipts")}
          </Button>
        </Upload>
        {camera}
      </Space>
    );
  }

  return (
    <div>
      <Upload.Dragger
        multiple
        accept={ACCEPT}
        showUploadList={false}
        disabled={disabled || busy}
        beforeUpload={(file, fileList) => {
          if (file === fileList[0]) send(fileList, "upload");
          return false;
        }}
        style={{ padding: 8 }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">{t("Drop receipts here or click to choose files")}</p>
        <p className="ant-upload-hint">
          {t(
            "PDF, JPG, PNG, WEBP or XRechnung/ZUGFeRD XML, up to 15 MB each, up to {{n}} files at once",
            {
              n: MAX_FILES
            }
          )}
        </p>
      </Upload.Dragger>
      <div style={{ marginTop: 8 }}>{camera}</div>
    </div>
  );
};

export default BelegUpload;
